import { Component } from '@angular/core';
import { ContentChildren } from '@angular/core';
import { ElementRef } from '@angular/core';
import { UIMenuItemComponent } from './menu-item.component';
import { OnDestroy } from '@angular/core';
import { QueryList } from '@angular/core';
import { TemplateRef } from '@angular/core';
import { ViewChild } from '@angular/core';
import { ViewContainerRef } from '@angular/core';

import { Observable, Subscription, merge } from 'rxjs';

import { Overlay } from 'app/ui/core/overlay';
import { OverlayRef } from 'app/ui/core/overlay-ref';
import { TemplatePortal } from 'app/ui/core/portal';

@Component({
    selector: 'ui-menu',
    template: '<ng-template><div class="ui-menu-container {{customClass}}"><ng-content></ng-content></div></ng-template>',
    styleUrls: ['menu.component.scss'],
    providers: [Overlay]
})
export class UIMenuComponent implements OnDestroy {

    private _backdropSubscription: Subscription;

    private _closeObserver;

    @ContentChildren(UIMenuItemComponent)
    private _items: QueryList<UIMenuItemComponent>;

    private _itemsSubscription: Subscription;

    private _overlayRef: OverlayRef;

    private _resizeListener;

    @ViewChild(TemplateRef)
    private _templateRef: TemplateRef<string>;

    private _triggeredFrom: ElementRef;

    customClass: string;

    constructor(private _elementRef: ElementRef,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef) {
        this.customClass = this._elementRef.nativeElement.className;
    }

    ngOnDestroy(): void {
        this._destroyComponent();
    }

    close() {
        this._destroyComponent();
    }

    open(triggeredFrom: ElementRef): Observable<void> {
        this._triggeredFrom = triggeredFrom;
        this._createOverlay();
        this._subscribeToBackdrop();
        this._subscribeToItems();
        this._subscribeToResizeListener();
        this._updatePosition();
        return Observable.create(observer => {
            this._closeObserver = observer;
        });
    }

    private _createOverlay(): void {
        if (!this._overlayRef) {
            const portal = new TemplatePortal(this._templateRef, this._viewContainerRef);
            this._overlayRef = this._overlay.create({ hasBackdrop: true });
            this._overlayRef.attach(portal);
        }
    }

    private _subscribeToBackdrop(): void {
        this._backdropSubscription = this._overlayRef.backdropClick().subscribe(() => {
            this._destroyComponent();
        });
    }

    private _subscribeToItems(): void {
        const emitters = merge(...this._items.map(option => option.onSelectItem));
        this._itemsSubscription = emitters.subscribe(() => {
            this.close();
        });
    }

    private _subscribeToResizeListener(): void {
        this._resizeListener = () => {
            this._updatePosition();
        };
        window.addEventListener('resize', this._resizeListener);
    }

    private _updatePosition() {
        const triggeredBox = this._triggeredFrom.nativeElement.getBoundingClientRect();
        const overlayElement = this._overlayRef.overlayElement;
        const overlayBox = overlayElement.getBoundingClientRect();
        overlayElement.style.left = (triggeredBox.right - overlayBox.width - 1) + window.scrollX + 'px';
        overlayElement.style.top = triggeredBox.bottom + window.scrollY + 'px';
    }

    private _destroyComponent(): void {
        this._destroyBackdrop();
        this._destroyOverlay();
        this._destroyItems();
        this._destroyResizeListener();
        this._destroyCloseObserver();
    }

    private _destroyBackdrop(): void {
        if (this._backdropSubscription) {
            this._backdropSubscription.unsubscribe();
            this._backdropSubscription = null;
        }
    }

    private _destroyCloseObserver(): void {
        if (this._closeObserver) {
            this._closeObserver.next();
            this._closeObserver.complete();
            this._closeObserver = null;
        }
    }

    private _destroyItems(): void {
        if (this._itemsSubscription) {
            this._itemsSubscription.unsubscribe();
            this._itemsSubscription = null;
        }
    }

    private _destroyOverlay(): void {
        if (this._overlayRef) {
            this._overlayRef.dispose();
            this._overlayRef = null;
        }
    }

    private _destroyResizeListener() {
        window.removeEventListener('resize', this._resizeListener);
    }

}